import { crypto } from '../../../lib/cep/node';
import {
  patchExchangeStore,
  readDesktopAppSnapshot,
  readExchangeStore,
  seedDesktopAppCookieIfAbsent,
} from './store.utils';

/**
 * Key the desktop app reads its anonymous PostHog id from
 * (`electron/packages/main/src/utils/cookies.utils.ts`).
 */
const DESKTOP_DISTINCT_ID_KEY = 'posthogDistinctId';

/**
 * `crypto.randomUUID` needs Node >= 14.17 and CEP's Node can be older, so it is
 * feature-detected rather than assumed. esbuild lowers syntax for `chrome74` but
 * never polyfills runtime APIs.
 */
export const generateUuidV4 = (): string => {
  try {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }

  try {
    const bytes = crypto.randomBytes(16);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
    const hex = bytes.toString('hex');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  } catch {
    /* fall through */
  }

  return `exchange-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export type ExchangeIdentity = {
  distinctId: string;
  isFirstOpen: boolean;
  firstSeenAt: string;
  openCount: number;
  /**
   * Where the id came from. `seeded` means it came back from the desktop app's
   * data file, but that file only exists because a previous panel open created
   * it — AutoCut itself is not installed yet.
   */
  idSource: 'desktop-app' | 'seeded' | 'local' | 'generated';
  /** Whether the AutoCut desktop app is really installed on this machine. */
  autocutDesktopPresent: boolean;
};

let cachedIdentity: ExchangeIdentity | undefined;

/**
 * Resolve the id every event is attributed to, and the first-open marker behind
 * the "installed" metric. Memoized, and never throws.
 *
 * Resolution order — desktop app first, so that:
 * - app absent, first open  -> mint, seed the app's file, app adopts our id;
 * - app already installed   -> adopt its id;
 * - repeat open             -> the seeded id comes back from the app's file.
 *
 * The local copy is only a backstop for when seeding the app's file fails
 * (permissions, corrupt file).
 */
export const resolveExchangeIdentity = (): ExchangeIdentity => {
  if (cachedIdentity) {
    return cachedIdentity;
  }

  const nowIso = new Date().toISOString();
  let identity: ExchangeIdentity = {
    distinctId: '',
    isFirstOpen: true,
    firstSeenAt: nowIso,
    openCount: 1,
    idSource: 'generated',
    autocutDesktopPresent: false,
  };

  try {
    const store = readExchangeStore();
    const desktopApp = readDesktopAppSnapshot();

    const distinctId =
      desktopApp.distinctId ?? store.posthogDistinctId ?? generateUuidV4();
    const idSource: ExchangeIdentity['idSource'] = desktopApp.distinctId
      ? desktopApp.isDesktopAppInstalled
        ? 'desktop-app'
        : 'seeded'
      : store.posthogDistinctId
      ? 'local'
      : 'generated';

    const isFirstOpen = !store.firstSeenAt;
    const openCount = (store.openCount ?? 0) + 1;

    identity = {
      distinctId,
      isFirstOpen,
      firstSeenAt: store.firstSeenAt ?? nowIso,
      openCount,
      idSource,
      autocutDesktopPresent: desktopApp.isDesktopAppInstalled,
    };

    patchExchangeStore({
      posthogDistinctId: distinctId,
      firstSeenAt: identity.firstSeenAt,
      lastSeenAt: nowIso,
      openCount,
    });

    // Hand the id to the desktop app so `exchange_*` events (AutoCut V3
    // project) and the app's own `aea_app_started` (desktop project) carry the
    // same distinct id. The two projects have separate person tables, so this
    // shared id is the only join key that makes the migration funnel
    // measurable. No-op when the file already carries an id.
    if (!desktopApp.distinctId) {
      seedDesktopAppCookieIfAbsent(DESKTOP_DISTINCT_ID_KEY, distinctId);
    }
  } catch (error) {
    console.warn('[analytics] resolveExchangeIdentity failed', error);
    identity = {
      ...identity,
      distinctId: identity.distinctId || generateUuidV4(),
    };
  }

  cachedIdentity = identity;
  return identity;
};

export const getExchangeIdentitySnapshot = (): ExchangeIdentity | undefined =>
  cachedIdentity;
